import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import FieldTools from '../FieldTools';
import PageManager from '../SheetBuilder/PageManager';
import Checkbox from '../../Forms/Checkbox/Checkbox';
import TagSelector from '../../Forms/TagSelector/TagSelector';
import Counter from '../../Forms/Counter/Counter';
import T, { Translate } from '../../i18n/i18n';
import { Container, Label, Subtitle, EditableToValue, Input, Select, ImageName, TextArea, ButtonGroup, Button, LabeledButtonGroup, LabeledButton, Row, FullHeight, EditorAddButton, EditorsList, EditorName, RemoveEditorButton, NoEditors, RemoveButton, ConfirmMessage } from './SheetProps.styles';
import { templateActions, filemanagerActions } from '../../../redux/actions';
import { createTemplates, updateTemplates, templateData, addEditorTotemplate, removeEditorFromTemplate, deleteTemplates } from '../../../services/templates';
import { sanitizeString } from '../../../services/strings';

const sanitizeInt = (value) => {
    if (typeof value === 'undefined') {
        return '';
    }
    return String(value);
}

export default () => {

    const dispatch = useDispatch();
    const history = useHistory();
    const routerParams = useParams();
    const loaded = useRef(false);
    const lastContent = useRef(false);

    const tool = useSelector(state => state.templateState.tool);
    const fieldProps = useSelector(state => state.templateState.props);
    const selectedId = useSelector(state => state.templateState.selectedId);
    const content = useSelector(state => state.templateState.content);
    const minPages = useSelector(state => state.templateState.minPages);
    const playerId = useSelector(state => state.authState.id);

    useEffect(()=>{
        setTemplateNumPages(n => Math.max(n ?? 1, minPages ?? 1));
    },[minPages]);

    const [templateId, setTemplateId] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [templateNumPages, setTemplateNumPages] = useState(null);
    const [templateIsPublic, setTemplateIsPublic] = useState(true);
    const [templateEditableTo, setTemplateEditableTo] = useState(0);
    const [templateSystem, setTemplateSystem] = useState([]);
    const [templateOwner, setTemplateOwner] = useState(null);
    const [selectedTool, setSelectedTool] = useState(null);
    const [visibleFields, setVisibleFields] = useState({});
    const [fieldType, setFieldType] = useState('');
    const [fieldLabel, setFieldLabel] = useState('');
    const [fieldName, setFieldName] = useState('');
    const [fieldNameId, setFieldNameId] = useState('');
    const [fieldDefaultValue, setFieldDefaultValue] = useState('');
    const [fieldOptions, setFieldOptions] = useState('');
    const [fieldIsAuto, setFieldIsAuto] = useState(false);
    const [fieldExpression, setFieldExpression] = useState('');
    const [fieldMacro, setFieldMacro] = useState('');
    const [fieldMacroCtrl, setFieldMacroCtrl] = useState('');
    const [fieldResourceId, setFieldResourceId] = useState('');
    const [fieldResourceName, setFieldResourceName] = useState('');
    const [fieldTextAlign, setFieldTextAlign] = useState('');
    const [fieldTextSize, setFieldTextSize] = useState('');
    const [fieldHasDetails, setFieldHasDetails] = useState(false);
    const [fieldX, setFieldX] = useState('');
    const [fieldY, setFieldY] = useState('');
    const [fieldWidth, setFieldWidth] = useState('');
    const [fieldHeight, setFieldHeight] = useState('');
    const [confirmRemove, setConfirmRemove] = useState(false);

    const [isChangeEditableTo, setIsChangeEditableTo] = useState(false);
    const [isChangeTagSystem, setIsChangeTagSystem] = useState(false);
    const [fieldEditableToEmail, setFieldEditableToEmail] = useState('');
    const [templateEditableToList, setTemplateEditableToList] = useState([]);
    
    const [hasChange, setHasChange] = useState(false);

    useEffect(() => {
        const contentString = JSON.stringify(content);
        setTemplateNumPages(s => s ?? PageManager.getNumberOfPages(content));

        if (lastContent.current !== contentString) {
            if (routerParams.templateId && loaded.current) {
                setHasChange(true);
            }

            if (!routerParams.templateId) {
                setHasChange(true);
            }
        }

        lastContent.current = contentString;
    }, [routerParams, content]);

    useEffect(() => {
        setFieldNameId(sanitizeString(fieldLabel));
    }, [fieldLabel]);

    useEffect(() => {
        setVisibleFields({
            defaultValue: ['TextField', 'TextSoloField', 'TextInt', 'TextFloat', 'TextArea', 'Select', 'Checkbox'].indexOf(fieldType) >= 0,
            options: ['Select'].indexOf(fieldType) >= 0,
            isAuto: ['TextField', 'TextSoloField', 'TextInt', 'TextFloat', 'TextArea', 'Select', 'Checkbox'].indexOf(fieldType) >= 0,
            expression: ['TextField', 'TextSoloField', 'TextInt', 'TextFloat', 'TextArea', 'Select', 'Checkbox'].indexOf(fieldType) >= 0,
            macro: ['TextField', 'Label', 'Image'].indexOf(fieldType) >= 0,
            resource: ['Image'].indexOf(fieldType) >= 0,
            textAlign: ['TextField', 'Label', 'TextSoloField', 'TextInt', 'TextFloat'].indexOf(fieldType) >= 0,
            textSize: ['TextField', 'Label', 'Select', 'TextArea', 'TextSoloField', 'TextInt', 'TextFloat'].indexOf(fieldType) >= 0,
            hasDetails: ['TextField', 'TextSoloField', 'TextInt', 'TextFloat'].indexOf(fieldType) >= 0,
        });
    }, [fieldType]);

    useEffect(() => {
        if (routerParams.templateId) {
            setTemplateId(routerParams.templateId);

            (async () => {
                let data = await templateData(routerParams.templateId);

                console.log(data);
                setTemplateName(data.name || '');
                setTemplateIsPublic(typeof data.isPublic == 'undefined' ? true : data.isPublic);
                setTemplateEditableTo(data.editableTo.length || 0);
                setTemplateSystem(data.system || []);
                setTemplateEditableToList(data.editableTo || []);
                setTemplateOwner(data.owner || '_');
                
                try {
                    let content = JSON.parse(data.content);
                    dispatch(templateActions.loadContent(content));
                } catch (e) {
                    console.error(Object.assign(e,{component:'TemplateBuilder/SheetProps', id: 'SheetContenParse'}));
                }

                setTimeout(() => {
                    loaded.current = true;
                },1000);
            })();
        }
    }, [dispatch, routerParams]);

    useEffect(() => {
        setSelectedTool(tool);

        if (tool) {
            dispatch(templateActions.setSelected({}, null));
            dispatch(templateActions.cleanFieldProp());
        }
    }, [dispatch, tool]);

    useEffect(() => {
        setFieldLabel(fieldProps.label || '');
        setFieldType(fieldProps.type || '');
        setFieldName(fieldProps.name || '');
        setFieldNameId(fieldProps.nameId || '');
        setFieldDefaultValue(fieldProps.defaultValue || '');
        setFieldOptions(fieldProps.options || '');
        setFieldIsAuto(fieldProps.auto || false);
        setFieldExpression(fieldProps.expression || '');
        setFieldMacro(fieldProps.macro || '');
        setFieldMacroCtrl(fieldProps.macroCtrl || '');
        setFieldResourceId(fieldProps.resourceId || '');
        setFieldResourceName(fieldProps.resourceName || '');
        setFieldTextAlign(fieldProps.textAlign || '');
        setFieldTextSize(fieldProps.textSize || '');
        setFieldHasDetails(fieldProps.hasDetails || false);
        setFieldX(sanitizeInt(fieldProps.x));
        setFieldY(sanitizeInt(fieldProps.y));
        setFieldWidth(sanitizeInt(fieldProps.width));
        setFieldHeight(sanitizeInt(fieldProps.height));
    }, [fieldProps]);

    const handleSetTool = (tool) => {
        let value = null;
        if (tool !== selectedTool) {
            value = tool;
        }

        setFieldLabel('');
        setFieldName('');
        setFieldNameId('');
        setFieldDefaultValue('');
        setFieldOptions('');
        setFieldIsAuto(false);
        setFieldExpression('');
        setFieldMacro('');
        setFieldMacroCtrl('');
        setFieldResourceId('');
        setFieldResourceName('');
        setFieldTextAlign('');
        setFieldTextSize('');
        setFieldHasDetails(false);
        setFieldX('');
        setFieldY('');
        setFieldWidth('');
        setFieldHeight('');
        dispatch(templateActions.setAddFieldTool(value));
    }

    const updateNumPages = (p) => {
        setTemplateNumPages(p)
        dispatch(templateActions.setNumPages(p));
    }

    const updateFieldType = (value) => {
        setFieldType(value);
        dispatch(templateActions.setFieldProp('type', value));
    }

    const updateFieldLabel = (value) => {
        let nameId = sanitizeString(value);
        setFieldLabel(value);
        setFieldNameId(nameId);
        dispatch(templateActions.setFieldProp('label', value));
        dispatch(templateActions.setFieldProp('nameId', nameId));
    }

    const updateFieldName = (value) => {
        setFieldName(value);
        dispatch(templateActions.setFieldProp('name', value));
    }

    const updateFieldDefaultValue = (value) => {
        setFieldDefaultValue(value);
        dispatch(templateActions.setFieldProp('defaultValue', value));
    }

    const updateFieldOptions = (value) => {
        setFieldOptions(value);
        dispatch(templateActions.setFieldProp('options', value));
    }

    const updateFieldIsAuto = (value) => {
        setFieldIsAuto(value);
        dispatch(templateActions.setFieldProp('auto', value));

        if (!value) {
            updateFieldExpression('');
        }
    };

    const updateFieldExpression = (value) => {
        setFieldExpression(value);
        dispatch(templateActions.setFieldProp('expression', value));
    }

    const updateFieldMacro = (value) => {
        setFieldMacro(value);
        dispatch(templateActions.setFieldProp('macro', value));
    }

    const updateFieldMacroCtrl = (value) => {
        setFieldMacroCtrl(value);
        dispatch(templateActions.setFieldProp('macroCtrl', value));
    }

    const updateFieldResource = (id, name) => {
        setFieldResourceId(id);
        setFieldResourceName(name);
        dispatch(templateActions.setFieldProp('resourceId', id));
        dispatch(templateActions.setFieldProp('resourceName', name));
    }

    const updateFieldTextAlign = (value) => {
        setFieldTextAlign(value);
        dispatch(templateActions.setFieldProp('textAlign', value));
    }

    const updateFieldTextSize = (value) => {
        setFieldTextSize(value);
        dispatch(templateActions.setFieldProp('textSize', value));
    }

    const updateFieldHasDetails = (value) => {
        setFieldHasDetails(Boolean(value));
        dispatch(templateActions.setFieldProp('hasDetails', Boolean(value)));
    }
    
    const updateFieldX = (value) => {
        setFieldX(value);
        dispatch(templateActions.setFieldProp('x', value));
    }

    const updateFieldY = (value) => {
        setFieldY(value);
        dispatch(templateActions.setFieldProp('y', value));
    }

    const updateFieldWidth = (value) => {
        setFieldWidth(value);
        dispatch(templateActions.setFieldProp('width', value));
    }

    const updateFieldHeight = (value) => {
        setFieldHeight(value);
        dispatch(templateActions.setFieldProp('height', value));
    }

    const handleSave = async () => {
        if (!isValidForm()) {
            return;
        }

        let data = {
            name: templateName,
            isPublic: templateIsPublic,
            content: content,
            system: templateSystem,
        };

        if (templateId) {
            await updateTemplates(templateId, data);
        } else {
            let result = await createTemplates(data);
            setTemplateId(result.id);
        }

        setHasChange(false);
    } 

    const isValidForm = () => {
        return templateName.length > 3;
    }

    const handleFindResource = () => {
        dispatch(filemanagerActions.openWindow('single', async (result) => {
            updateFieldResource(result[0].id, result[0].name);
        }));
    }

    const handlePressEmail = (e) => {
        if (e.which === 13) {
            handleAddEditor();
        }
    }

    const handleAddEditor = async () => {
        try {
            const res = await addEditorTotemplate(templateId, fieldEditableToEmail);
            setFieldEditableToEmail('');
            setTemplateEditableToList(res.editableTo || []);
            setTemplateEditableTo(res.editableTo.length || 0);
        } catch (e) {
            console.error(e);
        }
    }

    const handleRemoveEditor = async (id) => {
        try {
            const res = await removeEditorFromTemplate(templateId, id);
            setTemplateEditableToList(res.editableTo || []);
            setTemplateEditableTo(res.editableTo.length || 0);
        } catch (e) {
            console.error(e);
        }
    }

    const handleRemove = async () => {
        await deleteTemplates(templateId);
        history.goBack();
    }

    return <Container className="scrollbar">
        { isChangeEditableTo && !isChangeTagSystem ? <>
            <FullHeight>
                <Label><T value="templatebuilder.editors" /></Label>
                <Subtitle><T value="templatebuilder.editorsDescription" /></Subtitle>
                <Label><T value="templatebuilder.addEditorByEmail" /></Label>
                <Row align='flex-start'>
                    <Input value={fieldEditableToEmail} onChange={(e) => {setFieldEditableToEmail(e.target.value);}} onKeyPress={(e)=>{handlePressEmail(e)}} />
                    <EditorAddButton onClick={()=>{handleAddEditor()}}/>
                </Row>
                <EditorsList>
                    { templateEditableToList.length ? templateEditableToList.map(editor => <Row className={'highlight'} key={`editor-${editor.id}`}>
                        <EditorName>{editor.name ?? ''}</EditorName>
                        <RemoveEditorButton onClick={()=>{handleRemoveEditor(editor.id)}} />
                    </Row>) : <NoEditors>
                        <T value="templatebuilder.noEditors" />
                    </NoEditors> }
                </EditorsList>
            </FullHeight>
            <LabeledButtonGroup style={{padding:30}}>
                <LabeledButton color='#0cf' onClick={()=>{setIsChangeEditableTo(false)}}>
                    <T value={'back'} />
                </LabeledButton>
            </LabeledButtonGroup>
        </> : null }
        { !isChangeEditableTo && isChangeTagSystem ? <>
            <FullHeight>
                <Label><T value="templatebuilder.system" /></Label>
                <Subtitle><T value="templatebuilder.systemDescription" /></Subtitle>
                <TagSelector 
                    includedColor="#006CFF"
                    canExclude={false}
                    canAdd={true}
                    hideSubtitles={true}
                    tagGroup="gamesystems"
                    tagService="system"
                    onChange={(v) => {setTemplateSystem(v); setHasChange(true)}}
                    value={templateSystem}
                />
            </FullHeight>
            <LabeledButtonGroup style={{padding:30}}>
                <LabeledButton color='#0cf' onClick={()=>{setIsChangeTagSystem(false)}}>
                    <T value={'back'} />
                </LabeledButton>
            </LabeledButtonGroup>
        </> : null }
        { !isChangeEditableTo && !isChangeTagSystem ? <>
            <FullHeight>
                <Label><T value="templatebuilder.name" /></Label>
                <Input value={templateName} onChange={(e) => {setTemplateName(e.target.value); setHasChange(true);}}/>
                <Checkbox value={templateIsPublic} onClick={(value)=>{setTemplateIsPublic(value);setHasChange(true);}}><T value="templatebuilder.isPublic"/></Checkbox>
                <Row justify="space-between" align="flex-start">
                    <div>
                        <Label><T value="templatebuilder.editableTo" /></Label>
                        <EditableToValue>
                            {!!(templateEditableTo === 0) && <T value="templatebuilder.editableJustMe" />}
                            {!!(templateEditableTo === 1) && <>1 <T value="templatebuilder.editableOne" /></>}
                            {!!(templateEditableTo > 1) && <>{templateEditableTo} <T value="templatebuilder.editableMany" /></>}
                        </EditableToValue>
                    </div>
                    { Boolean(templateId) ? <div>
                        <LabeledButton className={"fixedPadding"} color='#f90' onClick={()=>{setIsChangeEditableTo(true)}}>
                            <T value={"templatebuilder.changeEditableTo"} />
                        </LabeledButton>
                    </div> : null }
                </Row>
                <Row justify="space-between" align="flex-start">
                    <div>
                        <Label><T value="templatebuilder.system" /></Label>
                        <EditableToValue>
                            { templateSystem && templateSystem.length >= 1 && templateSystem[0]["pt"] && templateSystem[0]["en"] ? templateSystem[0][Translate("lang")] : <T value="templatebuilder.noSystema" />}
                        </EditableToValue>
                    </div>
                    <div>
                        <LabeledButton className={"fixedPadding"} color='#39f' onClick={()=>{setIsChangeTagSystem(true)}}>
                            <T value={"templatebuilder.changeSystem"} />
                        </LabeledButton>
                    </div>
                </Row>
                <Label><T value="templatebuilder.pages" /></Label>
                <Counter min={Math.max(minPages, 1)} max={5} onChange={(v)=>{updateNumPages(v)}} value={templateNumPages} />
                <Label><T value="templatebuilder.addfield" /></Label>
                <ButtonGroup>
                    <Button className={selectedTool === FieldTools.TEXT ? 'selected' : ''} title={Translate('templatebuilder.component_line')} icon="\F07F5" onClick={() => {handleSetTool(FieldTools.TEXT)}} />
                    <Button className={selectedTool === FieldTools.TEXTSOLO ? 'selected' : ''} title={Translate('templatebuilder.component_textsolo')} icon="\F060E" onClick={() => {handleSetTool(FieldTools.TEXTSOLO)}} />
                    <Button className={selectedTool === FieldTools.LABEL ? 'selected' : ''} title={Translate('templatebuilder.component_label')} icon="\F0284" onClick={() => {handleSetTool(FieldTools.LABEL)}} />
                    <Button className={selectedTool === FieldTools.TEXT_INT ? 'selected' : ''} title={Translate('templatebuilder.component_textint')} icon="\F03A0" onClick={() => {handleSetTool(FieldTools.TEXT_INT)}} />
                    <Button className={selectedTool === FieldTools.TEXT_FLOAT ? 'selected' : ''} title={Translate('templatebuilder.component_textfloat')} icon="\F10A2" onClick={() => {handleSetTool(FieldTools.TEXT_FLOAT)}} />
                    <Button className={selectedTool === FieldTools.TEXTAREA ? 'selected' : ''} title={Translate('templatebuilder.component_textarea')} icon="\F1095" onClick={() => {handleSetTool(FieldTools.TEXTAREA)}} />
                    <Button className={selectedTool === FieldTools.SELECT ? 'selected' : ''} title={Translate('templatebuilder.component_select')} icon="\F1401" onClick={() => {handleSetTool(FieldTools.SELECT)}} />
                    <Button className={selectedTool === FieldTools.CHECKBOX ? 'selected' : ''} title={Translate('templatebuilder.component_checkbox')} icon="\F0C52" onClick={() => {handleSetTool(FieldTools.CHECKBOX)}} />
                    <Button className={selectedTool === FieldTools.IMAGE ? 'selected' : ''} title={Translate('templatebuilder.component_image')} icon="\F02E9" onClick={() => {handleSetTool(FieldTools.IMAGE)}} />
                    <Button className={selectedTool === FieldTools.GROUP ? 'selected' : ''} title={Translate('templatebuilder.component_group')} icon="\F0E5F" onClick={() => {handleSetTool(FieldTools.GROUP)}} />
                </ButtonGroup>

                <div style={{height:'50px'}}></div>

                {(selectedTool || selectedId) && <>
                    <Label><T value="templatebuilder.fieldtype" /></Label>
                    <Select value={fieldType} onMouseDown={(e) => {e.stopPropagation()}} onMouseUp={(e) => {e.stopPropagation()}} onChange={(e)=>{updateFieldType(e.target.value);}}>
                        <option value="TextField">{Translate("templatebuilder.component_line")}</option>
                        <option value="TextSoloField">{Translate("templatebuilder.component_textsolo")}</option>
                        <option value="Label">{Translate("templatebuilder.component_label")}</option>
                        <option value="TextInt">{Translate("templatebuilder.component_textint")}</option>
                        <option value="TextFloat">{Translate("templatebuilder.component_textfloat")}</option>
                        <option value="TextArea">{Translate("templatebuilder.component_textarea")}</option>
                        <option value="Select">{Translate("templatebuilder.component_select")}</option>
                        <option value="Checkbox">{Translate("templatebuilder.component_checkbox")}</option>
                        <option value="Image">{Translate("templatebuilder.component_image")}</option>
                        <option value="Group">{Translate("templatebuilder.component_group")}</option>
                    </Select>
                    <Label><T value="templatebuilder.fieldlabel" /></Label>
                    <Input value={fieldLabel} onChange={(e)=>{updateFieldLabel(e.target.value);}} />
                    <Label><T value="templatebuilder.fieldname" /></Label>
                    <Input value={fieldName || fieldNameId} onChange={(e)=>{updateFieldName(e.target.value)}} />
                    {visibleFields['defaultValue'] && <>
                        <Label><T value="templatebuilder.fielddefault" /></Label>
                        <Input value={fieldDefaultValue} onChange={(e)=>{updateFieldDefaultValue(e.target.value)}} />
                    </>}
                    {visibleFields['options'] && <>
                        <Label><T value="templatebuilder.fieldoptions" /></Label>
                        <TextArea value={fieldOptions} onChange={(e)=>{updateFieldOptions(e.target.value)}} />
                    </>}
                    {visibleFields['isAuto'] && <>
                        <Checkbox value={fieldIsAuto} onClick={(value)=>{updateFieldIsAuto(value)}}><T value="templatebuilder.isAuto"/></Checkbox>
                    </>}
                    {fieldIsAuto && visibleFields['expression'] && <>
                        <Label><T value="templatebuilder.expression" /></Label>
                        <Input value={fieldExpression} onChange={(e)=>{updateFieldExpression(e.target.value)}} />
                    </>}
                    {visibleFields['macro'] && <>
                        <Label><T value="templatebuilder.macro" /></Label>
                        <TextArea value={fieldMacro} onChange={(e)=>{updateFieldMacro(e.target.value)}} />
                        <Label><T value="templatebuilder.macromodctrl" /></Label>
                        <TextArea value={fieldMacroCtrl} onChange={(e)=>{updateFieldMacroCtrl(e.target.value)}} />
                    </>}
                    {visibleFields['resource'] && <>
                        <Label><T value="templatebuilder.image" /></Label>
                        <ImageName value={fieldResourceId} onClick={handleFindResource}>{fieldResourceName}</ImageName>
                    </>}
                    {visibleFields['textAlign'] && <>
                        <Label><T value="templatebuilder.textalign" /></Label>
                        <Select value={fieldTextAlign} onMouseDown={(e) => {e.stopPropagation()}} onMouseUp={(e) => {e.stopPropagation()}} onChange={(e) => {updateFieldTextAlign(e.target.value)}}>
                            <option value=""></option>
                            <option value="left">{Translate("templatebuilder.align-left")}</option>
                            <option value="center">{Translate("templatebuilder.align-center")}</option>
                            <option value="right">{Translate("templatebuilder.align-right")}</option>
                        </Select>
                    </>}
                    {visibleFields['textSize'] && <>
                        <Label><T value="templatebuilder.textsize" /></Label>
                        <Select value={fieldTextSize} onMouseDown={(e) => {e.stopPropagation()}} onMouseUp={(e) => {e.stopPropagation()}} onChange={(e) => updateFieldTextSize(e.target.value)}>
                            <option value=""></option>
                            <option value="0.8">{Translate("templatebuilder.size-small")}</option>
                            <option value="1">{Translate("templatebuilder.size-medium")}</option>
                            <option value="1.5">{Translate("templatebuilder.size-large")}</option>
                        </Select>
                    </>}
                    {visibleFields['hasDetails'] && <>
                        <Checkbox value={fieldHasDetails} onClick={(value)=>{updateFieldHasDetails(value)}}><T value="templatebuilder.hasDetails"/></Checkbox>
                    </>}
                    {selectedId && <>
                        <Row>
                            <Label><T value="templatebuilder.xpos" /></Label>
                            <Label><T value="templatebuilder.ypos" /></Label>
                            <Label><T value="templatebuilder.width" /></Label>
                            <Label><T value="templatebuilder.height" /></Label>
                        </Row>
                        <Row>
                            <Input value={fieldX} onChange={(e)=>{updateFieldX(e.target.value)}} />
                            <Input value={fieldY} onChange={(e)=>{updateFieldY(e.target.value)}} />
                            <Input value={fieldWidth} onChange={(e)=>{updateFieldWidth(e.target.value)}} />
                            <Input value={fieldHeight} onChange={(e)=>{updateFieldHeight(e.target.value)}} />
                        </Row>
                        <LabeledButtonGroup>
                            <LabeledButton color='#f44' onClick={()=>{handleSetTool(FieldTools.REMOVE)}}><T value="templatebuilder.removefield" /></LabeledButton>
                        </LabeledButtonGroup>
                    </>}
                </>}
            </FullHeight>
            {confirmRemove ? 
                <LabeledButtonGroup style={{padding:30}}>
                    <ConfirmMessage><T value={'templatemanager.confirmRemove'} /></ConfirmMessage>
                    <LabeledButton color='#f33' onClick={()=>{handleRemove()}}>
                        <T value='yes' />
                    </LabeledButton>
                    <LabeledButton color='#0c0' onClick={()=>{setConfirmRemove(false)}}>
                        <T value='no' />
                    </LabeledButton>
                </LabeledButtonGroup> 
            : 
                <LabeledButtonGroup style={{padding:30}}>
                    <LabeledButton className={isValidForm() && hasChange ? '' : 'inactive'} color='#0c0' onClick={()=>{handleSave()}}>
                        <T value={`templatebuilder.${hasChange?'save':'saved'}`} />
                    </LabeledButton>
                    { templateId && templateOwner === playerId ? <RemoveButton onClick={()=>{setConfirmRemove(true)}} title={Translate('remove')} /> : null }
                </LabeledButtonGroup>
            }
        </> : null}
    </Container>;
};